import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { FarmersService } from '~/api/services/farmers/index'
import Drawer from '~/components/Drawer'
import { FormFields } from '~/components/FormFields'
import { UserEl } from '~/pages/Dairy/DairyFactory/DairyFactoryProfile/Users/style'
import { GlobalEl } from '~/styles/app'
import { handleErrors } from '~/utils/helpers'

interface Props {
  handleHiddenDrawer: (submited?: boolean) => void
  dairyData: any
  co2Prefix: string
  climaCheckModuleEnabled: boolean
}

const DrawerEditDairy: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const { handleHiddenDrawer, dairyData, co2Prefix, climaCheckModuleEnabled } = props
  const { id } = useParams()

  const [isBusy, setIsBusy] = useState(false)

  useEffect(() => {
    if (dairyData?.amount !== undefined) {
      reset({
        ...dairyData
      })
    }
  }, [dairyData])

  const co2BillingData = [
    {
      label: t('Milk sold (in kg)'),
      min: 0,
      key: 'amount',
      max: 100000000,
      required: true,
      suffix: ' kg'
    },
    {
      label: t('Net milk price including surcharges'),
      decimalScale: 2,
      min: 0,
      required: true,
      max: 90,
      key: 'netPrice',
      suffix: ' € Ct/kg'
    },
    {
      label: t('Protein content of milk sold'),
      key: 'protein',
      required: true,
      decimalScale: 2,
      min: 2,
      max: 6,
      suffix: ' %'
    },
    {
      label: t('Fat content of milk sold'),
      key: 'fat',
      required: true,
      min: 3,
      max: 7,
      decimalScale: 2,
      suffix: ' %'
    },
    {
      label: t('Urea content'),
      key: 'ureaContent',
      required: false,
      min: 0,
      decimalScale: 2,
      suffix: ' ppm',
      max: 1000000
    }
  ]

  const climaCheckBillingData = [
    ...co2BillingData,
    {
      label: t('Urea content'),
      key: 'ureaContent',
      required: false,
      min: 0,
      decimalScale: 2,
      suffix: ' ppm',
      max: 1000000
    }
  ]

  const { handleSubmit, control, reset } = useForm({
    mode: 'onSubmit'
  })

  function renderDairyData() {
    const billingInputs = climaCheckModuleEnabled ? climaCheckBillingData : co2BillingData
    return <>
      {billingInputs.map(billing => {
        return <FormFields.Input
          key={billing.key}
          name={billing.key}
          typeInput={'controled'}
          allowDecimals={Boolean(billing?.decimalScale)}
          noErrorMessage
          withoutAsterisk
          decimalScale={billing?.decimalScale}
          control={control}
          suffix={billing.suffix}
          placeholder={billing.label}
          label={billing.label}
          rules={{
            required: billing.required,
            validate: (value) => {
              const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
              if(!transformValue && !billing.required) {
                return true
              }
              if (transformValue < billing.min || transformValue > billing.max) {
                return false
              }
              return true
            }
          }}
        />
      })}
      <UserEl.DrawerFooter>
        <GlobalEl.PrimaryButton>
          {t('Update')}
        </GlobalEl.PrimaryButton>
      </UserEl.DrawerFooter>
    </>
  }

  async function onSubmit(data) {
    setIsBusy(true)
    try {
      Object.keys(data).forEach(key => {
        if(data[key]) {
          data[key] = data[key].toString().replace(',', '.')
        }
      })
      data.month = dairyData?.month
      await FarmersService.updateDairyData(id, { amounts: [{ ...data }] }, co2Prefix)
      reset({
        amount: '',
        netPrice: '',
        protein: '',
        fat: '',
        ureaContent: ''
      });
      handleHiddenDrawer(true)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  return <Drawer
    content={renderDairyData()}
    header={dairyData ? dayjs(dairyData?.month).format('MMM YYYY') : ''}
    onHide={() => {
      reset({
        amount: '',
        netPrice: '',
        protein: '',
        fat: '',
        ureaContent: ''
      });
      handleHiddenDrawer();
    }}
    isBusy={isBusy}
    isVisible={Boolean(dairyData)}
    handleSubmit={handleSubmit(onSubmit)}
    closeOnEscape
  />
}
export default DrawerEditDairy